import { useEffect, useState } from "react";
import { Loader2, ScrollText, LogIn, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function LogTable({ url, columns }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(url).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [url]);
  if (!rows) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={row.id || i}>
              {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(row) : (row[c.key] ?? "—")}</TableCell>)}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={columns.length} className="text-center py-12 text-slate-400">{t("audit.noRecords")}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AuditLogs() {
  const { user } = useAuth();
  const t = useT();
  const isOwner = user.role === "owner";

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={t("audit.title")} subtitle={t("audit.subtitle")} />
      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="h-4 w-4 mr-2" />{t("audit.auditLog")}</TabsTrigger>
          {isOwner && <TabsTrigger value="logins" data-testid="tab-logins"><LogIn className="h-4 w-4 mr-2" />{t("audit.loginHistory")}</TabsTrigger>}
          {isOwner && <TabsTrigger value="perms" data-testid="tab-perms"><ShieldCheck className="h-4 w-4 mr-2" />{t("audit.permissionChanges")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="audit" className="mt-4">
          <LogTable url="/audit/logs" columns={[
            { key: "timestamp", label: t("audit.time"), render: (r) => <span className="text-sm text-slate-500">{fmt(r.timestamp)}</span> },
            { key: "actor_name", label: t("audit.employee"), render: (r) => <span className="font-medium">{r.actor_name || r.actor_username || "—"}</span> },
            { key: "action", label: t("audit.action"), render: (r) => <Badge variant="secondary" className="font-normal">{r.action}</Badge> },
            { key: "entity_type", label: t("audit.entity"), render: (r) => r.entity_type ? `${r.entity_type}` : "—" },
            { key: "ip", label: t("audit.ip") },
          ]} />
        </TabsContent>

        {isOwner && (
          <TabsContent value="logins" className="mt-4">
            <LogTable url="/audit/login-history" columns={[
              { key: "timestamp", label: t("audit.time"), render: (r) => <span className="text-sm text-slate-500">{fmt(r.timestamp)}</span> },
              { key: "username", label: t("audit.username") },
              { key: "success", label: t("audit.result"), render: (r) => r.success
                  ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("audit.success")}</Badge>
                  : <Badge variant="destructive">{t("audit.failed")}{r.reason ? ` · ${r.reason}` : ""}</Badge> },
              { key: "ip", label: t("audit.ip") },
            ]} />
          </TabsContent>
        )}

        {isOwner && (
          <TabsContent value="perms" className="mt-4">
            <LogTable url="/audit/permission-changes" columns={[
              { key: "timestamp", label: t("audit.time"), render: (r) => <span className="text-sm text-slate-500">{fmt(r.timestamp)}</span> },
              { key: "actor_username", label: t("audit.changedBy") },
              { key: "target_username", label: t("audit.targetEmployee") },
              { key: "overrides", label: t("audit.overrides"), render: (r) => <span className="text-sm text-slate-500">{Object.keys(r.overrides || {}).length} {t("audit.rules")}</span> },
            ]} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
